import React, { useEffect, useRef, useState } from 'react';
import { customerService, Company, CustomerUser, CustomerUserAttachment } from '../services/customerService';
import { organizationService, OrgMember } from '../services/organizationService';
import { projectStore } from '../lib/projectStore';
import { useAuth } from '../contexts/AuthContext';
import TaskDescriptionEditor from './TaskDescriptionEditor';
import '../styles/CustomerModal.css';

interface CustomerUserModalProps {
  customerUser?: CustomerUser | null;
  onClose: () => void;
  onSaved: (user: CustomerUser) => void;
}

type SectionKey = 'products' | 'support_tickets' | 'features' | 'subfeatures';
type TabKey = 'description' | 'insights' | 'history';

const CustomerUserModal: React.FC<CustomerUserModalProps> = ({ customerUser, onClose, onSaved }) => {
  const { currentOrg } = useAuth();
  const isEdit = !!customerUser;

  const [name, setName]         = useState(customerUser?.name ?? '');
  const [details, setDetails]   = useState(customerUser?.details ?? '');
  const [email, setEmail]       = useState(customerUser?.email ?? '');
  const [phone, setPhone]       = useState(customerUser?.phone ?? '');
  const [companyId, setCompanyId] = useState<string | null>(customerUser?.company_id ?? null);
  const [saving, setSaving]     = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('description');
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(new Set());
  const [showMenu, setShowMenu] = useState(false);

  // Assignee search (org member)
  const [assignee, setAssignee]               = useState<OrgMember | null>(null);
  const [assigneeQuery, setAssigneeQuery]     = useState('');
  const [allMembers, setAllMembers]           = useState<OrgMember[]>([]);
  const [showAssigneeSearch, setShowAssigneeSearch] = useState(false);

  // Company dropdown
  const [companies, setCompanies]             = useState<Company[]>([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companyQuery, setCompanyQuery]       = useState('');

  // Attachments
  const [attachments, setAttachments]   = useState<CustomerUserAttachment[]>([]);
  const [uploading, setUploading]       = useState(false);
  const [lightboxUrl, setLightboxUrl]   = useState<string | null>(null);

  const fileInputRef  = useRef<HTMLInputElement>(null);
  const menuRef       = useRef<HTMLDivElement>(null);
  const assigneeInputRef = useRef<HTMLInputElement>(null);
  const companyRef    = useRef<HTMLDivElement>(null);

  // Load companies
  useEffect(() => {
    customerService.getCompanies(projectStore.getProjectId()).then(setCompanies).catch(() => {});
  }, []);

  // Load org members for assignee
  useEffect(() => {
    if (!currentOrg) return;
    organizationService.getMembersWithProfiles(currentOrg.id)
      .then(members => {
        setAllMembers(members);
        if (customerUser?.assignee_id) {
          const match = members.find(m => m.user_id === customerUser.assignee_id);
          if (match) setAssignee(match);
        }
      })
      .catch(() => {});
  }, [currentOrg, customerUser]);

  // Load existing attachments when editing
  useEffect(() => {
    if (customerUser?.id) {
      customerService.getUserAttachments(customerUser.id).then(setAttachments).catch(() => {});
    }
  }, [customerUser?.id]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const h = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMenu]);

  // Close assignee search on outside click
  useEffect(() => {
    if (!showAssigneeSearch) return;
    const h = (e: MouseEvent) => {
      if (assigneeInputRef.current && !assigneeInputRef.current.parentElement?.contains(e.target as Node)) setShowAssigneeSearch(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showAssigneeSearch]);

  // Close company dropdown on outside click
  useEffect(() => {
    if (!showCompanyDropdown) return;
    const h = (e: MouseEvent) => { if (companyRef.current && !companyRef.current.contains(e.target as Node)) setShowCompanyDropdown(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showCompanyDropdown]);

  const assigneeMatches = allMembers.filter(m => {
    const q = assigneeQuery.trim().toLowerCase();
    if (!q) return true;
    return (m.full_name ?? '').toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q);
  }).slice(0, 8);

  const companyMatches = companies.filter(c => {
    const q = companyQuery.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q);
  }).slice(0, 10);

  const selectedCompany = companies.find(c => c.id === companyId) ?? null;

  const toggleSection = (key: SectionKey) => {
    setOpenSections(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !currentOrg) return;
    e.target.value = '';
    if (!customerUser?.id) { alert('Please save the user first, then add attachments.'); return; }
    setUploading(true);
    try {
      const uploaded = await Promise.all(files.map(f => customerService.uploadUserAttachment(customerUser.id, currentOrg.id, f)));
      setAttachments(prev => [...prev, ...uploaded]);
    } catch (err) {
      console.error('Upload failed:', err);
      alert(`Failed to upload: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = async (att: CustomerUserAttachment) => {
    if (!window.confirm(`Remove "${att.file_name}"?`)) return;
    try {
      await customerService.deleteUserAttachment(att);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch {
      alert('Failed to remove attachment.');
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { alert('User name is required'); return; }
    setSaving(true);
    try {
      const extra = { assignee_id: assignee?.user_id ?? null, email: email.trim() || null, phone: phone.trim() || null };
      let saved: CustomerUser;
      if (isEdit && customerUser) {
        saved = await customerService.updateCustomerUser(customerUser.id, {
          name: name.trim(), details: details.trim(), company_id: companyId, ...extra,
        });
      } else {
        saved = await customerService.createCustomerUser(
          name.trim(), details.trim(), projectStore.getProjectId(), companyId, extra
        );
      }
      onSaved(saved);
    } catch (err) {
      console.error('Error saving user:', err);
      alert('Failed to save user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const sections: { key: SectionKey; label: string }[] = [
    { key: 'products',        label: 'Products' },
    { key: 'support_tickets', label: 'Support Tickets' },
    { key: 'features',        label: 'Features' },
    { key: 'subfeatures',     label: 'Subfeatures' },
  ];

  const isImage = (type: string) => type.startsWith('image/');

  return (
    <>
      <div className="cum-modal-backdrop" onClick={() => !saving && onClose()}>
        <div className="inline-edit-form premium-task-modal cum-company-modal" onClick={e => e.stopPropagation()}>

          {/* ── Header ── */}
          <div className="task-modal-header">
            <div className="header-titles-new">
              <h3>{isEdit ? 'Edit User' : 'Add User'}</h3>
              <p className="header-subtitle-new">Manage contact details and linked records</p>
            </div>
            <div className="cum-header-actions">
              <div className="cum-menu-wrapper" ref={menuRef}>
                <button className="task-modal-close cum-dots-btn" onClick={() => setShowMenu(p => !p)} title="More options" disabled={saving}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                  </svg>
                </button>
                {showMenu && (
                  <div className="cum-context-menu">
                    <button className="cum-menu-item" onClick={() => { navigator.clipboard.writeText(window.location.href).catch(() => {}); setShowMenu(false); }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                      Copy link
                    </button>
                    <div className="cum-menu-divider" />
                    <button className="cum-menu-item" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                      Duplicate
                    </button>
                    <button className="cum-menu-item cum-menu-item-muted" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      Archive
                    </button>
                    <button className="cum-menu-item cum-menu-item-danger" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      Delete user
                    </button>
                  </div>
                )}
              </div>
              <button type="button" className="task-modal-close" onClick={onClose} disabled={saving}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          {/* ── Two-column content ── */}
          <div className="task-modal-content-layout">

            {/* LEFT COLUMN */}
            <div className="task-modal-main-column">

              <div className="form-group-new">
                <label className="section-label-new">User Name <span style={{ color: '#dc2626' }}>*</span></label>
                <div className="title-input-wrapper-new">
                  <input
                    type="text"
                    className="task-title-input-new"
                    placeholder="Enter user name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              {/* Tabs: Description | Insights | History */}
              <div className="cum-tabs cum-tabs-inline">
                {(['description', 'insights', 'history'] as TabKey[]).map(tab => (
                  <button key={tab} type="button" className={`cum-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {activeTab === 'description' && (
                <>
                  <div className="form-group-new">
                    <TaskDescriptionEditor
                      key={`user-${customerUser?.id ?? 'new'}`}
                      htmlValue={details}
                      onChange={setDetails}
                      placeholder="Add a description — role, background, notes…"
                    />
                  </div>

                  {/* Attachment previews */}
                  {attachments.length > 0 && (
                    <div className="cum-attachment-previews">
                      {attachments.map(att => (
                        <div key={att.id} className="cum-att-thumb">
                          {isImage(att.file_type) ? (
                            <img src={att.url} alt={att.file_name} className="cum-att-img" onClick={() => setLightboxUrl(att.url)} title={att.file_name} />
                          ) : (
                            <div className="cum-att-file" title={att.file_name}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              <span className="cum-att-file-name">{att.file_name.length > 14 ? att.file_name.slice(0, 12) + '…' : att.file_name}</span>
                            </div>
                          )}
                          <button type="button" className="cum-att-remove" onClick={() => handleRemoveAttachment(att)} title="Remove">
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                      {uploading && (
                        <div className="cum-att-thumb cum-att-uploading">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" className="cum-att-spinner"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeDasharray="40" strokeDashoffset="20"/></svg>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Attachment button */}
                  <div className="form-group-new">
                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={handleFileChange} />
                    <div className="integration-buttons-grid-new">
                      <div className="integrations-grid-row-new">
                        <button type="button" className="integration-btn-new" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                          </svg>
                          {uploading ? 'Uploading…' : 'Attachment'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Linked sections */}
                  <div className="cum-sections-list">
                    {sections.map(({ key, label }) => {
                      const open = openSections.has(key);
                      return (
                        <div key={key} className="cum-section">
                          <button type="button" className="cum-section-header" onClick={() => toggleSection(key)}>
                            <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                            <span className="cum-section-label">{label}</span>
                            <span className="cum-section-count">0</span>
                            <button type="button" className="cum-section-add" onClick={e => e.stopPropagation()} title={`Add ${label.toLowerCase()}`}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                              </svg>
                            </button>
                          </button>
                          {open && <div className="cum-section-empty">No {label.toLowerCase()} linked yet</div>}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {activeTab === 'insights' && (
                <div className="cum-insights-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>
                  <p>Insights will appear here once linked to this user.</p>
                </div>
              )}

              {activeTab === 'history' && (
                <div className="cum-insights-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <p>Activity history will appear here.</p>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN */}
            <div className="task-modal-sidebar-column">

              {/* Assignee — org member — TOP */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Assignee</label>
                <div style={{ position: 'relative' }}>
                  {showAssigneeSearch ? (
                    <div className="cum-owner-search-box">
                      <input
                        ref={assigneeInputRef}
                        className="task-title-input-new cum-owner-input"
                        type="text"
                        placeholder="Search members…"
                        value={assigneeQuery}
                        onChange={e => setAssigneeQuery(e.target.value)}
                        autoFocus
                      />
                      <div className="cum-owner-dropdown">
                        <button type="button" className="cum-owner-option" onClick={() => { setAssignee(null); setAssigneeQuery(''); setShowAssigneeSearch(false); }}>
                          <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No assignee</span>
                        </button>
                        {assigneeMatches.length === 0 && assigneeQuery.trim() && <div className="cum-owner-empty">No members found</div>}
                        {assigneeMatches.map(m => (
                          <button key={m.id} type="button" className="cum-owner-option" onClick={() => { setAssignee(m); setAssigneeQuery(''); setShowAssigneeSearch(false); }}>
                            <span className="cum-owner-avatar">{(m.full_name || m.email || '?')[0].toUpperCase()}</span>
                            <div className="cum-owner-info">
                              <span className="cum-owner-name">{m.full_name || 'Unnamed'}</span>
                              <span className="cum-owner-email">{m.email}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="sidebar-select-trigger-new has-icon-new cum-owner-trigger" onClick={() => setShowAssigneeSearch(true)}>
                      {assignee ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="cum-owner-avatar cum-owner-avatar-sm">{(assignee.full_name || assignee.email || '?')[0].toUpperCase()}</span>
                          <span>{assignee.full_name || assignee.email}</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="cum-owner-circle" />
                          <span style={{ color: '#94a3b8' }}>Not assigned</span>
                        </div>
                      )}
                      <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Company — searchable dropdown */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Company</label>
                <div style={{ position: 'relative' }} ref={companyRef}>
                  <button
                    type="button"
                    className="sidebar-select-trigger-new has-icon-new cum-owner-trigger"
                    onClick={() => { setShowCompanyDropdown(p => !p); setCompanyQuery(''); }}
                  >
                    {selectedCompany ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="cum-company-dot" />
                        <span>{selectedCompany.name}</span>
                      </div>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>Not assigned</span>
                    )}
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  {showCompanyDropdown && (
                    <div className="cum-owner-dropdown">
                      <div style={{ padding: '6px 8px 4px' }}>
                        <input
                          className="task-title-input-new cum-owner-input"
                          type="text"
                          placeholder="Search companies…"
                          value={companyQuery}
                          onChange={e => setCompanyQuery(e.target.value)}
                          autoFocus
                          style={{ fontSize: 13, fontWeight: 400 }}
                        />
                      </div>
                      <button type="button" className="cum-owner-option" onClick={() => { setCompanyId(null); setShowCompanyDropdown(false); }}>
                        <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No company</span>
                      </button>
                      {companyMatches.map(c => (
                        <button key={c.id} type="button" className="cum-owner-option" onClick={() => { setCompanyId(c.id); setShowCompanyDropdown(false); }}>
                          <span className="cum-company-dot" style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: '#111827' }}>{c.name}</span>
                        </button>
                      ))}
                      {companyMatches.length === 0 && <div className="cum-owner-empty">No companies found</div>}
                    </div>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Email</label>
                <input className="sidebar-select-trigger-new cum-sidebar-text-input" type="email" placeholder="user@example.com" value={email} onChange={e => setEmail(e.target.value)} />
              </div>

              {/* Phone (Contact) */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Contact</label>
                <input className="sidebar-select-trigger-new cum-sidebar-text-input" type="tel" placeholder="+1 555 000 0000" value={phone} onChange={e => setPhone(e.target.value)} />
              </div>

            </div>
          </div>

          {/* ── Footer ── */}
          <div className="task-modal-footer">
            <button className="cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="submit-btn" onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add user'}
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="cum-lightbox" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="attachment" className="cum-lightbox-img" onClick={e => e.stopPropagation()} />
          <button className="cum-lightbox-close" onClick={() => setLightboxUrl(null)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}
    </>
  );
};

export default CustomerUserModal;
